import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import MainContainer from "../components/MainContainer";
import { FiPlus, FiMinus, FiTrash2, FiShoppingCart } from "react-icons/fi";
import { AnimatePresence, motion } from "framer-motion";
import {
  FaAt,
  FaBars,
  FaMapMarkerAlt,
  FaPhone,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import { priceFormatter } from "../utils/utils";
import { useDispatch, useSelector } from "react-redux";
import {
  clearCart,
  decrementQuantity,
  incrementQuantity,
  removeFromCart,
  selectCart,
} from "../store/slices/cart/slice";
import { FRONT_URL } from "../utils/constants";
import { createOrder } from "../store/slices/orders/asyncThunks";
import { useNavigate } from "react-router-dom";
import { selectOrder } from "../store/slices/orders/slice";
import { checkCoupon } from "../store/slices/coupons/asyncThunks";
import {
  resetAppliedCoupon,
  selectCoupons,
} from "../store/slices/coupons/slice";
import bundlesService from "../api/services/bundles";

const CartPage = () => {
  const dispatch = useDispatch();
  const { loading } = useSelector(selectOrder);
  const [showModal, setShowModal] = useState(false);
  const { applied, checkCouponLoading } = useSelector(selectCoupons);
  const [couponCode, setCouponCode] = useState("");
  const cart = useSelector(selectCart);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [details, setDetails] = useState("");
  const [bundles, setBundles] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    bundlesService
      .getActiveBundles()
      .then((res) => setBundles(res.data || []))
      .catch(() => setBundles([]));
  }, []);

  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const addressRef = useRef(null);
  const cityRef = useRef(null);
  const zipCodeRef = useRef(null);
  const phoneRef = useRef(null);
  const emailRef = useRef(null);

  const shippingPrice = 600;

  const total = cart.data.reduce((acc, item) => {
    const priceToUse =
      item.salePrice !== undefined &&
      item.salePrice !== null &&
      item.salePrice !== ""
        ? Number(item.salePrice)
        : Number(item.price);

    return acc + priceToUse * item.quantity;
  }, 0);

  // --- Bundle popusti ---
  const cartIds = new Set(cart.data.map((item) => item.id));

  // Aktivni bundlovi gde su OBA proizvoda u korpi -> popust se primenjuje
  const matchedBundles = bundles.filter(
    (b) => cartIds.has(b.productAId) && cartIds.has(b.productBId)
  );
  const bundleDiscount = matchedBundles.reduce(
    (sum, b) => sum + Number(b.discountAmount),
    0
  );

  // Bundlovi gde je samo JEDAN proizvod u korpi -> predlog ("dodaj X i ustedi Y")
  const bundleSuggestions = bundles
    .filter(
      (b) =>
        (cartIds.has(b.productAId) && !cartIds.has(b.productBId)) ||
        (!cartIds.has(b.productAId) && cartIds.has(b.productBId))
    )
    .map((b) => {
      const missing = cartIds.has(b.productAId) ? b.productB : b.productA;
      return {
        id: b.id,
        missingId: missing?.id,
        missingName: missing?.name,
        amount: Number(b.discountAmount),
      };
    })
    .filter((s) => s.missingName);

  const isCouponActive = !!applied;
  const discountPercent = isCouponActive ? applied.discount : 0;
  const discountAmount = total * (discountPercent / 100);
  const discountedTotal = Math.max(total - discountAmount - bundleDiscount, 0);
  const totalWithShipping = discountedTotal + shippingPrice;

  const freeShippingThreshold = 7000;
  const remainingForFreeShipping = freeShippingThreshold - discountedTotal;
  const isEligibleForFreeShipping = remainingForFreeShipping <= 0;

  const increment = ({ id, selectedSize }) => {
    dispatch(incrementQuantity({ id, selectedSize }));
  };

  const decrement = ({ id, selectedSize }) => {
    dispatch(decrementQuantity({ id, selectedSize }));
  };

  const removeItem = ({ id, selectedSize }) => {
    dispatch(removeFromCart({ id, selectedSize }));
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") setShowModal(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  useEffect(() => {
    if (showModal) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "auto";
    }
  }, [showModal]);

  const handleNumberInput = (setter) => (e) => {
    const onlyNums = e.target.value.replace(/\D/g, "");
    setter(onlyNums);
  };

  const handleCreateOrder = () => {
    if (!firstName.trim()) {
      firstNameRef.current?.focus();
      return;
    }

    if (!lastName.trim()) {
      lastNameRef.current?.focus();
      return;
    }

    if (!address.trim()) {
      addressRef.current?.focus();
      return;
    }

    if (!city.trim()) {
      cityRef.current?.focus();
      return;
    }

    if (!zipCode.trim()) {
      zipCodeRef.current?.focus();
      return;
    }

    if (!phone.trim()) {
      phoneRef.current?.focus();
      return;
    }

    if (!email.trim()) {
      emailRef.current?.focus();
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      emailRef.current?.focus();
      return;
    }

    const orderData = {
      firstName,
      lastName,
      address,
      city,
      zipCode,
      phone,
      email,
      products: cart.data,
      couponId: applied?.id,
    };

    if (details.trim()) orderData.details = details.trim();

    dispatch(createOrder(orderData))
      .unwrap()
      .then(() => {
        navigate("/uspesna-porudzbina");
        dispatch(clearCart());
        dispatch(resetAppliedCoupon());
      })
      .catch(() => {
        console.log("Greška pri porudžbini");
      });
  };

  const handleApplyCoupon = () => {
    if (couponCode.trim()) {
      dispatch(checkCoupon(couponCode.trim().toUpperCase()))
        .unwrap()
        .then((res) => {
          setCouponCode("");
        })
        .catch((err) => console.log(err.message));
    }
  };

  return (
    <MainContainer hideLogo>
      <ClearRow>
        <CartTitle>
          Tvoja korpa <span>買</span>
          {cart.data.length > 0 && (
            <small>
              {cart.data.reduce((acc, item) => acc + item.quantity, 0)}{" "}
              artikala
            </small>
          )}
        </CartTitle>
        {cart.data.length > 0 && (
          <ClearCartButton
            onClick={() => {
              dispatch(clearCart());
            }}
          >
            <FiTrash2 size={14} />
            Obriši korpu
          </ClearCartButton>
        )}
      </ClearRow>

      <CartGrid>
        <ItemsCol>
          {cart.data.length === 0 ? (
            <EmptyWrap>
              <EmptyText>
                <i>空</i>
                Korpa je prazna — vreme je da to promeniš.
              </EmptyText>
              <OrderButton
                onClick={() => {
                  navigate("/prodavnica");
                  setTimeout(() => {
                    window.scrollTo(0, 0);
                  }, 300);
                }}
              >
                Pogledaj proizvode
              </OrderButton>
            </EmptyWrap>
          ) : (
            cart.data.map((item) => (
              <ItemRow key={`${item.id}-${item.selectedSize}`}>
                <ItemThumb
                  src={`${FRONT_URL}/thumbnails/${item.thumbnailImage}`}
                  alt={item.name}
                  onClick={() => {
                    navigate(`/proizvod/${item.id}`);
                    setTimeout(() => {
                      window.scrollTo(0, 0);
                    }, 300);
                  }}
                />
                <ItemInfo>
                  <ItemName
                    onClick={() => {
                      navigate(`/proizvod/${item.id}`);
                      setTimeout(() => {
                        window.scrollTo(0, 0);
                      }, 300);
                    }}
                  >
                    {item.name}
                  </ItemName>
                  <ItemMeta>
                    Veličina: {item.selectedSize} · Boja:{" "}
                    <ColorCircle $color={item.color} />
                  </ItemMeta>
                  <ItemUnit>
                    {item?.salePrice ? (
                      <>
                        <s>{priceFormatter(item.price)} RSD</s>{" "}
                        {priceFormatter(item.salePrice)} RSD
                      </>
                    ) : (
                      <>{priceFormatter(item.price)} RSD</>
                    )}
                  </ItemUnit>
                </ItemInfo>

                <QtyBox>
                  <button
                    onClick={() =>
                      decrement({
                        id: item.id,
                        selectedSize: item.selectedSize,
                      })
                    }
                  >
                    <FiMinus size={14} />
                  </button>
                  <span>{item.quantity}</span>
                  <button
                    onClick={() =>
                      increment({
                        id: item.id,
                        selectedSize: item.selectedSize,
                      })
                    }
                  >
                    <FiPlus size={14} />
                  </button>
                </QtyBox>

                <LineTotal>
                  {priceFormatter(
                    (item?.salePrice || item.price) * item.quantity
                  )}{" "}
                  RSD
                </LineTotal>

                <RemoveX
                  onClick={() =>
                    removeItem({ id: item.id, selectedSize: item.selectedSize })
                  }
                >
                  <FiTrash2 size={15} />
                </RemoveX>
              </ItemRow>
            ))
          )}
        </ItemsCol>
        {cart.data.length > 0 && (
          <Summary>
            <SummaryTitle>Pregled porudžbine</SummaryTitle>

            <SRow>
              <span>Međuzbir</span>
              <b>{priceFormatter(total)} RSD</b>
            </SRow>

            {isCouponActive && (
              <SRow $accent>
                <span>Kupon (-{discountPercent}%)</span>
                <b>-{priceFormatter(discountAmount)} RSD</b>
              </SRow>
            )}

            {bundleDiscount > 0 && (
              <SRow $green>
                <span>Bundle popust</span>
                <b>-{priceFormatter(bundleDiscount)} RSD</b>
              </SRow>
            )}

            <STotal>
              <span>Ukupno</span>
              <b>{priceFormatter(discountedTotal)} RSD</b>
            </STotal>

            <ShipProgress>
              <ShipText $done={isEligibleForFreeShipping}>
                {isEligibleForFreeShipping ? (
                  <>✓ Ostvarili ste BESPLATNU dostavu!</>
                ) : (
                  <>
                    Još <b>{priceFormatter(remainingForFreeShipping)} RSD</b> do
                    besplatne dostave
                  </>
                )}
              </ShipText>
              <ShipTrack>
                <ShipFill
                  $done={isEligibleForFreeShipping}
                  style={{
                    width: `${Math.min(
                      100,
                      (discountedTotal / freeShippingThreshold) * 100
                    )}%`,
                  }}
                />
              </ShipTrack>
            </ShipProgress>

            {bundleSuggestions.map((s) => (
              <BundleSuggestion
                key={s.id}
                onClick={() => {
                  navigate(`/proizvod/${s.missingId}`);
                  setTimeout(() => window.scrollTo(0, 0), 300);
                }}
              >
                Dodaj <strong>{s.missingName}</strong> i uštedi{" "}
                {priceFormatter(s.amount)} RSD!
              </BundleSuggestion>
            ))}

            <OrderButton
              onClick={() => {
                if (cart.data.length > 0) {
                  navigate("/checkout");
                  window.scrollTo({ top: 0, behavior: "instant" });
                }
              }}
            >
              Poruči odmah
            </OrderButton>

            <PayNote>
              Plaćanje pouzećem · Lično preuzimanje u Novom Sadu
            </PayNote>
          </Summary>
        )}
      </CartGrid>

      <AnimatePresence>
        {showModal && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <ModalContent
              as={motion.div}
              initial={{ y: "100vh" }}
              animate={{ y: 0 }}
              exit={{ y: "100vh" }}
              transition={{ type: "spring", stiffness: 100 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn onClick={() => setShowModal(false)}>
                <FaTimes />
              </CloseBtn>
              <ModalHeader>Potvrda porudžbine</ModalHeader>

              <ModalSection>
                <ModalRow>
                  <PriceModal>Cena proizvoda</PriceModal>
                  <PriceModal $bold>
                    {priceFormatter(discountedTotal)} RSD
                  </PriceModal>
                </ModalRow>
                {isCouponActive && (
                  <ModalRow style={{ marginTop: -10, marginBottom: 5 }}>
                    <PriceModal
                      style={{
                        opacity: 0,
                        userSelect: "none",
                        pointerEvents: "none",
                      }}
                    >
                      Cena proizvoda
                    </PriceModal>
                    <PriceModal
                      $bold
                      style={{
                        color: "#ff6a5e",
                        textDecoration: "line-through",
                      }}
                    >
                      {priceFormatter(total)} RSD
                    </PriceModal>
                  </ModalRow>
                )}
                <ModalRow style={{ marginTop: -10 }}>
                  <PriceModal>Isporuka</PriceModal>
                  {!isEligibleForFreeShipping ? (
                    <PriceModal $bold>
                      {priceFormatter(shippingPrice)} RSD
                    </PriceModal>
                  ) : (
                    <PriceModal $bold>BESPLATNA</PriceModal>
                  )}
                </ModalRow>
                <ModalRow $total>
                  <TotalModal>Ukupno</TotalModal>
                  {!isEligibleForFreeShipping ? (
                    <TotalModal>
                      {priceFormatter(totalWithShipping)} RSD
                    </TotalModal>
                  ) : (
                    <TotalModal>
                      {priceFormatter(totalWithShipping - shippingPrice)} RSD
                    </TotalModal>
                  )}
                </ModalRow>
              </ModalSection>

              <Form>
                <Row $gap>
                  <Row>
                    <ModalInputWrapper>
                      <FaUser size={15} />
                    </ModalInputWrapper>
                    <ModalInput
                      required
                      ref={firstNameRef}
                      placeholder="Unesite ime"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </Row>

                  <ModalInput
                    ref={lastNameRef}
                    required
                    placeholder="Unesite prezime"
                    $small
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Row>

                <Row>
                  <ModalInputWrapper>
                    <FaMapMarkerAlt size={15} />
                  </ModalInputWrapper>
                  <ModalInput
                    ref={addressRef}
                    required
                    placeholder="Unesite ulicu i broj"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                  />
                </Row>

                <Row $gap>
                  <Row>
                    <ModalInputWrapper>
                      <FaMapMarkerAlt size={15} />
                    </ModalInputWrapper>
                    <ModalInput
                      ref={cityRef}
                      required
                      placeholder="Unesite mesto"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                    />
                  </Row>

                  <ModalInput
                    ref={zipCodeRef}
                    required
                    placeholder="Unesite poštanski broj"
                    $small
                    value={zipCode}
                    onChange={handleNumberInput(setZipCode)}
                  />
                </Row>

                <Row>
                  <ModalInputWrapper>
                    <FaPhone size={15} />
                  </ModalInputWrapper>
                  <ModalInput
                    ref={phoneRef}
                    required
                    placeholder="Unesite kontakt telefon"
                    value={phone}
                    onChange={handleNumberInput(setPhone)}
                  />
                </Row>

                <Row>
                  <ModalInputWrapper>
                    <FaAt size={15} />
                  </ModalInputWrapper>
                  <ModalInput
                    ref={emailRef}
                    required
                    type="email"
                    placeholder="E-mail adresa"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Row>
                <Row>
                  <ModalInputWrapper>
                    <FaBars size={15} />
                  </ModalInputWrapper>
                  <ModalTextarea
                    placeholder="Napomena (nije obavezna)"
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                  />
                </Row>

                <CouponWrapper>
                  <ModalInput
                    value={couponCode}
                    onChange={(e) =>
                      setCouponCode(e.target.value.toUpperCase())
                    }
                    placeholder="Kupon kod"
                  />
                  {checkCouponLoading ? (
                    <CheckoutButton>
                      Aktiviraj
                      <l-line-spinner
                        size="15"
                        stroke="1.5"
                        speed="1"
                        color="#fff"
                      />
                    </CheckoutButton>
                  ) : (
                    <CheckoutButton onClick={handleApplyCoupon}>
                      Aktiviraj
                    </CheckoutButton>
                  )}
                </CouponWrapper>

                {loading ? (
                  <AddToCart $disabled>
                    Završite porudžbinu
                    <l-line-spinner
                      size="16"
                      stroke="2"
                      speed="1"
                      color="#fe5f55"
                      style={{ position: "absolute", right: "2rem" }}
                    />
                  </AddToCart>
                ) : (
                  <AddToCart onClick={handleCreateOrder}>
                    <FiShoppingCart size={20} />
                    Završite porudžbinu
                  </AddToCart>
                )}
              </Form>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </MainContainer>
  );
};

export default CartPage;

const red = "#fe5f55";
const darkBg = "rgba(28, 28, 30, 1)";

const CloseBtn = styled.div`
  position: absolute;
  top: 2.4rem;
  right: 2.4rem;
  background: var(--bg1, #101013);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  color: #fff;
  border-radius: 50%;
  font-size: 1.4rem;
  width: 3.4rem;
  height: 3.4rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s var(--ease, ease);

  &:hover {
    background: var(--red, #fe5f55);
    border-color: var(--red, #fe5f55);
    color: var(--bg0, #0a0a0c);
  }
`;
const AddToCart = styled.button`
  color: ${(props) =>
    props.$disabled ? "var(--muted, #8e8c94)" : "var(--bg0, #0a0a0c)"};
  padding: 1.5rem;
  border-radius: 0.4rem;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 1.3rem;
  width: 100%;
  margin-top: 1rem;
  transition: background 0.3s var(--ease, ease);

  display: flex;
  align-items: center;
  flex-direction: row;
  position: relative;
  gap: 1rem;
  justify-content: center;
  align-self: center;

  svg {
    width: 1.6rem;
    height: 1.6rem;
  }

  background: ${(props) =>
    props.$disabled ? "rgba(23, 23, 28, 0.85)" : "var(--red, #fe5f55)"};
  border: 1px solid
    ${(props) =>
      props.$disabled ? "var(--line2, rgba(255,255,255,0.14))" : "transparent"};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};

  &:hover {
    background: ${(props) =>
      props.$disabled ? "rgba(23, 23, 28, 0.85)" : "var(--red-deep, #e04a40)"};
  }
`;
const CartTitle = styled.h1`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  font-size: clamp(3.2rem, 4.6vw, 4.8rem);
  line-height: 1;
  letter-spacing: 0.01em;
  color: var(--text, #f4f2ee);

  span {
    font-family: var(--font-kanji);
    font-size: 0.5em;
    color: var(--red, #fe5f55);
    vertical-align: middle;
    margin-left: 0.6rem;
  }

  small {
    font-size: 1.3rem;
    font-family: "Poppins-Medium";
    letter-spacing: 0.08em;
    color: var(--muted, #8e8c94);
    margin-left: 1.4rem;
    vertical-align: middle;
  }

  @media (max-width: 1030px) {
    text-align: center;
  }
`;

const EmptyText = styled.span`
  min-height: 13.2rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;

  i {
    font-style: normal;
    font-family: var(--font-kanji);
    font-size: 7rem;
    color: var(--muted2, #5c5a63);
    line-height: 1;
  }

  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 120rem;
  font-size: 1.2rem;
  font-family: "Poppins-Medium";
  color: white;
`;

const CheckoutButton = styled.button`
  background: transparent;
  width: fit-content;
  color: var(--red, #fe5f55);
  height: 4rem;
  padding: 1rem 1.8rem;
  border: 1px solid rgba(254, 95, 85, 0.5);
  border-radius: 0.4rem;
  position: relative;
  text-align: center;
  font-size: 1.1rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  align-self: ${(props) => (props.$center ? "center" : "flex-start")};
  font-family: "Anton", sans-serif;
  font-weight: 400;
  min-height: 4rem;
  cursor: pointer;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  transition: all 0.25s var(--ease, ease);

  &:hover {
    background: var(--red, #fe5f55);
    color: var(--bg0, #0a0a0c);
  }
`;

const CartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  /* min-height: 52.15rem; */
  width: 100%;
  align-items: center;
  padding: 2rem;
  max-width: 120rem;
  padding-top: 0rem;
  @media (max-width: 1030px) {
    justify-content: center;
    flex-wrap: wrap;
    flex-direction: row;
  }
`;

const CartItem = styled.div`
  width: 100%;
  max-width: 100rem;
  display: flex;
  gap: 2rem;
  background: ${darkBg};
  border-radius: 1.2rem;
  padding: 1.5rem;
  position: relative;
  box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.25);

  border: 1px solid #2a2a2a;

  @media (max-width: 1030px) {
    flex-wrap: wrap;
  }

  @media (max-width: 450px) {
    align-items: center;
    flex-direction: column;
  }
`;
const CartRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2rem;
  flex: 1;
  @media (max-width: 450px) {
    justify-content: space-between;
    width: 100%;
  }
`;
const Image = styled.img`
  width: 10rem;
  height: 10rem;
  border-radius: 1.2rem;
  object-fit: cover;
  @media (max-width: 1030px) {
    width: 20rem;
    height: 20rem;
  }
`;
const TotalColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-bottom: 2rem;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  @media (max-width: 450px) {
    align-items: ${(props) => (props.$flexEnd ? "flex-end" : "flex-start")};
  }
`;

const Label = styled.div`
  font-size: 1.6rem;
  color: ${red};
  font-family: "Poppins-SemiBold";
  margin-bottom: 0.3rem;
`;

const ColorLabel = styled.span`
  display: flex;
  font-family: "Poppins-Regular";

  align-items: center;
  gap: 0.4rem;
  font-size: 1.2rem;
  color: #aaa;
`;

const ColorCircle = styled.div`
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  border: 1px solid #fff;
`;

const ItemTitle = styled.div`
  font-family: "Poppins-SemiBold";
  font-size: 1.4rem;
  color: #fff;
  cursor: pointer;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  max-height: calc(1.4em * 2);

  &:hover {
    color: #fe8f88;
  }
`;

const Price = styled.div`
  color: #fff;
  font-size: 1.4rem;
  font-family: "Poppins-SemiBold";
  white-space: nowrap;
`;

const PriceOld = styled.span`
  font-size: 1.4rem;
  font-family: "Poppins-Regular";
  white-space: nowrap;
  color: #999;
  text-decoration: line-through;
`;

const PriceColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  button {
    background: none;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${red};
    min-width: 3rem;
    min-height: 3rem;
    max-width: 3rem;
    max-height: 3rem;
    border-radius: 0.4rem;
    color: ${red};
    cursor: pointer;
    font-size: 1.4rem;
    box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.25);

    &:hover {
      background-color: ${red};
      color: white;
    }
  }

  span {
    min-width: 2rem;
    font-family: "Poppins-SemiBold";
    text-align: center;
    font-size: 1.4rem;
    color: #fff;
  }
`;

const RemoveBtn = styled.button`
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${red};
  min-width: 3rem;
  min-height: 3rem;
  max-width: 3rem;
  max-height: 3rem;
  border-radius: 0.4rem;
  color: ${red};
  cursor: pointer;
  box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.25);
  font-size: 1.2rem;
  &:hover {
    background-color: ${red};
    color: white;
  }
  @media (max-width: 450px) {
    position: absolute;
    top: 2rem;
    right: 2rem;
  }
`;

const TotalWrapper = styled.div`
  margin-top: 2rem;
  width: 100%;
  max-width: 100rem;
  align-items: flex-end;
  flex-direction: column;
  display: flex;
  @media (max-width: 1030px) {
    align-items: center;
  }
`;

const TotalText = styled.div`
  font-size: 1.8rem;
  font-family: "Poppins-SemiBold";
  color: #fff;
`;

const CartGrid = styled.div`
  display: grid;
  grid-template-columns: 1.8fr 1fr;
  gap: 3.6rem;
  align-items: start;
  width: 100%;
  max-width: 124rem;
  padding: 0 2rem;

  @media (max-width: 1030px) {
    grid-template-columns: 1fr;
  }
`;

const ItemsCol = styled.div`
  width: 100%;
`;

const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 8.8rem 1fr auto 12rem 3.2rem;
  gap: 1.8rem;
  align-items: center;
  padding: 1.8rem 1rem;
  border-bottom: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-radius: 0.4rem;
  transition: background 0.25s;

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  &:first-child {
    border-top: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  }

  @media (max-width: 640px) {
    grid-template-columns: 6.4rem 1fr auto;
    grid-template-rows: auto auto;

    & > *:nth-child(4) {
      grid-column: 2;
      text-align: left;
    }
  }
`;

const ItemThumb = styled.img`
  width: 8.8rem;
  height: 10.4rem;
  object-fit: cover;
  border-radius: 0.4rem;
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  cursor: pointer;

  @media (max-width: 640px) {
    width: 6.4rem;
    height: 7.6rem;
  }
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const ItemName = styled.div`
  font-size: 1.5rem;
  font-family: "Poppins-SemiBold";
  color: var(--text, #f4f2ee);
  cursor: pointer;
  transition: color 0.25s;

  &:hover {
    color: var(--red, #fe5f55);
  }
`;

const ItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.2rem;
  font-family: "Poppins-Regular";
  color: var(--muted, #8e8c94);
`;

const ItemUnit = styled.div`
  font-size: 1.2rem;
  font-family: "Poppins-Medium";
  color: var(--muted, #8e8c94);

  s {
    color: var(--muted2, #5c5a63);
    margin-right: 0.4rem;
  }
`;

const QtyBox = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  border-radius: 0.4rem;

  button {
    width: 3.4rem;
    height: 3.6rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    color: var(--muted, #8e8c94);
    cursor: pointer;
    transition: color 0.25s;

    &:hover {
      color: var(--red, #fe5f55);
    }
  }

  span {
    min-width: 2.8rem;
    text-align: center;
    font-size: 1.4rem;
    font-family: "Poppins-SemiBold";
    color: var(--text, #f4f2ee);
  }
`;

const LineTotal = styled.div`
  font-family: "Anton", sans-serif;
  font-size: 1.7rem;
  letter-spacing: 0.02em;
  color: var(--text, #f4f2ee);
  min-width: 11rem;
  text-align: right;
`;

const RemoveX = styled.button`
  background: transparent;
  border: none;
  color: var(--muted2, #5c5a63);
  cursor: pointer;
  padding: 0.8rem;
  display: flex;
  transition: color 0.25s;

  &:hover {
    color: var(--red, #fe5f55);
  }
`;

const Summary = styled.div`
  background: var(--bg2, #17171c);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-radius: 0.6rem;
  padding: 2.6rem;
  position: sticky;
  top: 2rem;

  @media (max-width: 1030px) {
    position: static;
  }
`;

const SummaryTitle = styled.h3`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  font-size: 2rem;
  letter-spacing: 0.04em;
  color: var(--text, #f4f2ee);
  margin-bottom: 2rem;
`;

const SRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.8rem 0;
  font-size: 1.4rem;
  font-family: "Poppins-Regular";
  color: ${(props) =>
    props.$green
      ? "#7dd490"
      : props.$accent
        ? "var(--red, #fe5f55)"
        : "var(--muted, #8e8c94)"};

  b {
    font-family: "Poppins-SemiBold";
    color: ${(props) =>
      props.$green
        ? "#7dd490"
        : props.$accent
          ? "var(--red, #fe5f55)"
          : "var(--text, #f4f2ee)"};
  }
`;

const STotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  border-top: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  margin-top: 1.4rem;
  padding-top: 1.8rem;
  font-size: 1.5rem;
  font-family: "Poppins-Medium";
  color: var(--text, #f4f2ee);

  b {
    font-family: "Anton", sans-serif;
    font-weight: 400;
    font-size: 2.6rem;
    letter-spacing: 0.02em;
    color: var(--red, #fe5f55);
  }
`;

const PayNote = styled.p`
  font-size: 1.2rem;
  font-family: "Poppins-Regular";
  color: var(--muted2, #5c5a63);
  text-align: center;
  margin-top: 1.4rem;
  line-height: 1.7;
`;

const EmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.4rem;
  padding: 4rem 0;
`;

const ShipProgress = styled.div`
  width: 100%;
  margin: 1.6rem 0 0.4rem;
`;

const ShipText = styled.div`
  font-size: 1.3rem;
  font-family: "Poppins-Medium";
  color: ${(props) => (props.$done ? "#7dd490" : "var(--muted, #8e8c94)")};
  margin-bottom: 0.8rem;

  b {
    color: var(--red, #fe5f55);
    font-family: "Poppins-Bold";
  }
`;

const ShipTrack = styled.div`
  height: 0.4rem;
  background: var(--bg3, #1e1e24);
  border-radius: 10rem;
  overflow: hidden;
`;

const ShipFill = styled.div`
  height: 100%;
  background: ${(props) =>
    props.$done
      ? "linear-gradient(90deg, #5cb878, #7dd490)"
      : "linear-gradient(90deg, var(--red-deep, #e04a40), var(--red, #fe5f55))"};
  border-radius: 10rem;
  transition: width 0.6s var(--ease, ease);
`;

const BundleSuggestion = styled.div`
  font-size: 1.2rem;
  font-family: "Poppins-Medium";
  color: var(--red, #fe5f55);
  cursor: pointer;
  padding: 1rem 1.2rem;
  border: 1px dashed rgba(254, 95, 85, 0.5);
  border-radius: 0.4rem;
  width: 100%;
  margin-top: 1.4rem;
  line-height: 1.6;

  strong {
    font-family: "Poppins-SemiBold";
  }

  &:hover {
    background: rgba(254, 95, 85, 0.1);
  }
`;

const OrderButton = styled.button`
  background: var(--red, #fe5f55);
  width: 100%;
  max-width: 36rem;
  color: var(--bg0, #0a0a0c);
  padding: 1.5rem 2rem;
  border: none;
  border-radius: 0.4rem;
  font-size: 1.3rem;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  cursor: pointer;
  margin-top: 2rem;
  transition: all 0.3s var(--ease, ease);

  &:hover {
    background: var(--red-deep, #e04a40);
  }
`;

// Modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  background: rgba(6, 6, 8, 0.8);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

const ModalContent = styled.div`
  background: var(--bg2, #17171c);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  padding: 3rem;
  border-radius: 0.6rem;
  width: 90%;
  position: relative;
  max-width: 52rem;
  max-height: 92vh;
  overflow-y: auto;
  z-index: 2001;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ModalHeader = styled.h2`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: 2.4rem;
  color: var(--text, #f4f2ee);
  text-align: left;

  &::after {
    content: " 買";
    font-family: var(--font-kanji);
    font-size: 0.6em;
    color: var(--red, #fe5f55);
    vertical-align: middle;
  }
`;

const ModalSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  padding: 1.6rem;
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-radius: 0.4rem;
  background: var(--bg1, #101013);
`;

const ModalRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: space-between;
  gap: 2rem;
  padding: ${(props) => (props.$total ? "1rem 0 0" : "0")};
  margin-top: ${(props) => (props.$total ? "0.6rem" : "0")};
  border-top: ${(props) =>
    props.$total
      ? "1px solid var(--line2, rgba(255, 255, 255, 0.14))"
      : "none"};
`;

// Coupon and Confirmation
const CouponWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
  justify-content: flex-start;
  margin-top: 1rem;

  input {
    border-radius: 0.4rem;
  }
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TotalModal = styled.span`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  font-size: 2.2rem;
  letter-spacing: 0.02em;
  color: var(--red, #fe5f55);

  &:first-child {
    font-size: 1.5rem;
    font-family: "Poppins-Medium";
    color: var(--text, #f4f2ee);
  }
`;
const PriceModal = styled.span`
  color: ${(props) =>
    props.$bold ? "var(--text, #f4f2ee)" : "var(--muted, #8e8c94)"};
  font-family: ${(props) =>
    props.$bold ? "Poppins-SemiBold" : "Poppins-Regular"};
  font-size: 1.4rem;
`;

const ModalInputWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  border-right: none;
  border-radius: 0.4rem 0 0 0.4rem;
  padding: 0 1.2rem;
  background: var(--bg1, #101013);
  height: 4.2rem;
  flex-shrink: 0;

  svg {
    color: var(--red, #fe5f55);
  }
`;

const ModalInput = styled.input`
  padding: 1rem 1.2rem;
  font-size: 1.4rem;
  font-family: "Poppins-Regular";
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  border-radius: ${(props) => (props.$small ? "0.4rem" : "0 0.4rem 0.4rem 0")};
  flex: 1;
  background: var(--bg1, #101013);
  color: var(--text, #f4f2ee);
  outline: none;
  width: 100%;
  height: 4.2rem;
  transition: border-color 0.25s;

  &::placeholder {
    color: var(--muted2, #5c5a63);
  }

  &:focus {
    border-color: var(--red, #fe5f55);
  }
`;

const ModalTextarea = styled.textarea`
  padding: 1rem 1.2rem;
  font-size: 1.4rem;
  font-family: "Poppins-Regular";
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  border-radius: 0 0.4rem 0.4rem 0;
  resize: vertical;
  height: 4.2rem;
  min-height: 4.2rem;
  overflow: hidden;
  width: 100%;
  color: var(--text, #f4f2ee);
  background: var(--bg1, #101013);
  outline: none;
  transition: border-color 0.25s;

  &::placeholder {
    color: var(--muted2, #5c5a63);
  }

  &:focus {
    border-color: var(--red, #fe5f55);
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${(props) => (props.$gap ? "1rem" : "0rem")};
  width: ${(props) => (props.$gap ? "100%" : "unset")};
  flex: ${(props) => (props.$gap ? "1" : "unset")};
  @media (max-width: 500px) {
    flex-direction: ${(props) => (props.$gap ? "column" : "row")};
  }
`;
const ClearRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 2rem;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
  padding: 0 2rem;
  max-width: 124rem;
  margin-bottom: 1.6rem;
`;
const ClearCartButton = styled.button`
  background: transparent;
  color: var(--muted, #8e8c94);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  padding: 0.9rem 1.6rem;
  border-radius: 0.4rem;
  font-size: 1.1rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-family: "Poppins-Medium";
  cursor: pointer;
  transition: all 0.25s var(--ease, ease);
  display: flex;
  align-items: center;
  gap: 0.7rem;
  flex-direction: row;
  flex-shrink: 0;

  &:hover {
    color: var(--red, #fe5f55);
    border-color: var(--red, #fe5f55);
    background: transparent;
  }
`;
